import React from "react"
import { Link } from "react-router-dom"

import { useNowHand } from "./now_page/use_now_hand"
import { MoveCardState, MoveKey, NEW_PROJECT_SENTINEL } from "./now_page/types"
import ReadCard from "./now_page/cards/read_card"
import ExtractCard from "./now_page/cards/extract_card"
import PlaceCard from "./now_page/cards/place_card"
import PolishCard from "./now_page/cards/polish_card"
import WanderCard from "./now_page/cards/wander_card"
import ProjectCard from "./now_page/cards/project_card"
import RerollIcon from "./now_page/reroll_icon"

/** Capture → extract → place */
const LEFT_MOVES: MoveKey[] = ["read", "extract", "place"]
/** Polish incomplete ideas, then wander / continue */
const RIGHT_MOVES: MoveKey[] = ["polish", "wander", "project"]

export default function NowPage() {
  const { cards, initialLoadComplete, redrawAll, reroll } = useNowHand()

  if (!initialLoadComplete) {
    return (
      <div className="relative flex min-h-0 flex-auto items-center justify-center bg-stone-100 pt-16 dark:bg-gray-950">
        <div className="relative text-sm text-stone-500 dark:text-stone-400">
          Loading…
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-0 flex-auto overflow-y-auto bg-stone-100 pt-16 dark:bg-gray-950">
      {/* Quiet room — no grain, soft wash only */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_60%_at_50%_0%,rgba(255,255,255,0.85),transparent_55%)] dark:bg-[radial-gradient(90%_60%_at_50%_0%,rgba(255,255,255,0.04),transparent_55%)]"
        aria-hidden
      />

      <div className="relative mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-12">
        <header className="mb-8 flex items-center justify-between gap-4">
          <h1 className="text-[1.75rem] font-semibold tracking-[-0.03em] text-stone-900 dark:text-white">
            Now
          </h1>
          <button
            type="button"
            onClick={redrawAll}
            className="inline-flex h-9 items-center gap-2 rounded-full bg-white px-3.5 text-[13px] font-medium text-stone-700 shadow-sm ring-1 ring-stone-900/10 transition-colors hover:bg-stone-50 hover:text-stone-900 dark:bg-gray-900 dark:text-stone-200 dark:ring-white/10 dark:hover:bg-gray-800"
            data-testid="now-shuffle"
            aria-label="Redraw all"
          >
            <RerollIcon className="opacity-80" />
            Redraw
          </button>
        </header>

        {cards.length === 0 ? (
          <EmptyNow />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 md:items-start">
            <div className="flex flex-col gap-4">
              {cards
                .filter((c) => LEFT_MOVES.includes(c.move))
                .map((card) => (
                  <NowCardSlot
                    key={card.move}
                    card={card}
                    onReroll={() => reroll(card.move)}
                    onObjectGone={() => reroll(card.move)}
                  />
                ))}
            </div>
            <div className="flex flex-col gap-4">
              {cards
                .filter((c) => RIGHT_MOVES.includes(c.move))
                .map((card) => (
                  <NowCardSlot
                    key={card.move}
                    card={card}
                    onReroll={() => reroll(card.move)}
                    onObjectGone={() => reroll(card.move)}
                  />
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function NowCardSlot({
  card,
  onReroll,
  onObjectGone,
}: {
  card: MoveCardState
  onReroll: () => void
  onObjectGone: () => void
}) {
  const common = { onReroll, onObjectGone }
  switch (card.move as MoveKey) {
    case "read":
      return <ReadCard sourceId={card.objectId} {...common} />
    case "extract":
      return <ExtractCard sourceId={card.objectId} {...common} />
    case "place":
      return <PlaceCard fleetingId={card.objectId} {...common} />
    case "polish":
      return <PolishCard ideaId={card.objectId} {...common} />
    case "wander":
      return <WanderCard ideaId={card.objectId} {...common} />
    case "project":
      return (
        <ProjectCard
          projectId={card.objectId}
          isNewInvitation={card.objectId === NEW_PROJECT_SENTINEL}
          {...common}
        />
      )
  }
}

function EmptyNow() {
  return (
    <div className="rounded-lg bg-white px-8 py-14 text-center shadow-sm ring-1 ring-stone-900/10 dark:bg-gray-900 dark:ring-white/10">
      <p className="text-[15px] leading-relaxed text-stone-600 dark:text-stone-300">
        Nothing to draw right now.
      </p>
      <div className="mt-5 flex justify-center gap-5 text-[13px]">
        <Link
          to="/app/capture"
          className="font-medium text-stone-800 underline decoration-stone-300 underline-offset-4 hover:decoration-stone-600 dark:text-stone-200"
        >
          Capture
        </Link>
        <Link
          to="/app/sources"
          className="font-medium text-stone-800 underline decoration-stone-300 underline-offset-4 hover:decoration-stone-600 dark:text-stone-200"
        >
          Sources
        </Link>
      </div>
    </div>
  )
}
